import { useMemo, useState } from 'react';
import cn from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Size } from '@Components/designConstants';
import { EmptyState } from '@Components/EmptyState';
import { EntityKind, EntityLogo } from '@Components/EntityLogo';
import { getHeatColumn, HeatCell } from '@Components/MetricsTable/HeatCell';
import { hasNumber } from '@Components/MetricsTable/metrics-table-utils';
import { Spinner } from '@Components/Spinner';
import { BPMTable, TypedHeader } from '@Components/StickyTable/BPMTable';
import { CellRenderer, SuperTopItemType } from '@Components/StickyTable/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { TablePage } from '@Components/TablePage';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import {
  SPOT_LENGTHS,
  STAGE_ONE_PER_DOLLAR,
  STAGE_ONE_RAW_EFFECT,
  STAGE_TWO_PER_DOLLAR,
  StageTwoPath,
  StageTwoRow
} from './linear-ml-diagnostics-constants';
import { getStageTwoValue } from './linear-ml-diagnostics-utils';
import { useStageTwoDiagnostics } from './use-linear-ml-diagnostics';
import './StageTwoDiagnostics.scss';

const BASE_METRICS = [STAGE_ONE_RAW_EFFECT, STAGE_ONE_PER_DOLLAR, STAGE_TWO_PER_DOLLAR];

interface HeaderConfig {
  path: StageTwoPath;
  superLabel: string;
}

const formatValue = (val: number | null | undefined): string =>
  hasNumber(val) ? val.toLocaleString('en-US', { maximumFractionDigits: 3 }) : '-';

const buildHeaders = (
  rows: StageTwoRow[],
  creatives: string[],
  showCreatives: boolean
): [TypedHeader<StageTwoRow>[], SuperTopItemType<string>[]] => {
  const superHeaders: SuperTopItemType<string>[] = [{ span: 4, data: '' }];
  const headers: TypedHeader<StageTwoRow>[] = [
    {
      label: 'Network',
      name: 'network',
      width: 110,
      sortPriority: 1,
      sortAscending: true,
      renderer: row => <NetworkCell network={row.network} />
    },
    { label: 'Avail', name: 'avail', width: 80, sortPriority: 2 },
    { label: 'Rotation ID', name: 'rotation_id', width: 115, sortPriority: 3, sortAscending: true },
    { label: 'Rotation Name', name: 'rotation_name', flex: 2, minFlexWidth: 140 }
  ];

  const headerConfig: HeaderConfig[] = [
    { path: ['ad_raw_effect'], superLabel: STAGE_ONE_RAW_EFFECT },
    { path: ['adeffect_per_dollar'], superLabel: STAGE_ONE_PER_DOLLAR },
    { path: ['stageTwoAvg'], superLabel: STAGE_TWO_PER_DOLLAR },
    ...(showCreatives
      ? creatives.map((creative): HeaderConfig => ({
          path: ['stageTwoByCreative', creative],
          superLabel: creative
        }))
      : [])
  ];

  for (const { path, superLabel } of headerConfig) {
    superHeaders.push({ span: 2, data: superLabel });
    for (const length of SPOT_LENGTHS) {
      const values = rows.map(row => getStageTwoValue(row, length, path));
      const heatColumn = getHeatColumn(values, values.map(formatValue));

      headers.push({
        label: `:${length}`,
        name: `${superLabel} ${length}`,
        flex: 1,
        minFlexWidth: 120,
        nonFilterable: true,
        contentGetter: row => getStageTwoValue(row, length, path) ?? 0,
        renderer: row => {
          const val = getStageTwoValue(row, length, path);
          const unseenCombo =
            path[0] === 'stageTwoAvg' &&
            hasNumber(val) &&
            !hasNumber(getStageTwoValue(row, length, ['ad_raw_effect']));
          return (
            <HeatCell
              label={formatValue(val)}
              value={val}
              column={heatColumn}
              overlayText={unseenCombo ? 'Not seen in Stage One' : undefined}
            />
          );
        }
      });
    }
  }

  return [headers, superHeaders];
};

interface StageTwoDiagnosticsProps {
  kpi: string | undefined;
  branch: string;
}

const renderSuperHeader: CellRenderer<string> = ({ data: label, style, classes }) => (
  <div
    style={style}
    className={cn(classes, 'bpm-super-header', {
      'bpm-super-header-with-data': Boolean(label),
      'creative-super-header': label && !BASE_METRICS.includes(label)
    })}
  >
    {label}
  </div>
);

export const StageTwoDiagnostics = ({ kpi, branch }: StageTwoDiagnosticsProps) => {
  const { data, error, isError } = useStageTwoDiagnostics(kpi, branch);
  const [showCreatives, setShowCreatives] = useState(false);

  const [headers, superHeaders] = useMemo(
    () => buildHeaders(data?.rows ?? [], data?.creatives ?? [], showCreatives),
    [data, showCreatives]
  );

  // Always render the table so its control strip stays usable while loading or empty.
  const renderNoRows = () => {
    if (isError) {
      return <EmptyState title="Couldn't load Stage Two data" description={error.message} />;
    }
    if (!data) {
      return <Spinner />;
    }
    if (!data.rows) {
      return (
        <EmptyState
          title='No Stage Two data'
          description={`There is no stage two output for ${kpi} on branch ${branch}.`}
        />
      );
    }
    return <TableEmptyState message='No rotations to show' />;
  };

  const creativeCount = data?.creatives?.length ?? 0;
  return (
    <TablePage className='stage-two-diagnostics' dataTestId='stage-two-table'>
      <BPMTable
        alternateColors
        noRowsRenderer={renderNoRows}
        data={data?.rows ?? []}
        superHeaders={superHeaders}
        superHeadersRenderer={renderSuperHeader}
        headers={headers}
        additionalControls={
          <div className='stage-two-controls'>
            <ToggleSwitch
              size={Size.LG}
              label={`Show creatives (${creativeCount})`}
              checked={showCreatives}
              onChange={setShowCreatives}
              disabled={creativeCount === 0}
              data-testid='show-creatives-toggle'
            />
          </div>
        }
      />
    </TablePage>
  );
};

interface NetworkCellProps {
  network: string;
}

const NetworkCell = ({ network }: NetworkCellProps) => (
  <OverlayTrigger placement='right' overlay={<Tooltip>{network}</Tooltip>}>
    <div className='stage-two-network-logos'>
      <EntityLogo kind={EntityKind.NETWORK} id={network} size={Size.MD} />
    </div>
  </OverlayTrigger>
);
